import { cx } from "@emotion/css";
import { Badge } from "@mui/material";
import List from "@mui/material/List";
import { ArrowUpDown, Filter } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import MenuLUI from "src/components/Default/LekUI/Menu";
import isPes from "src/helpers/isPes";
import AddCommentField from "./AddCommentField";
import CommentItem from "./CommentItem";

type SortKey = "ASC" | "DESC" | "LIKES";
type TypeKey = "BASE" | null;

interface CommentFilters {
	sort: SortKey;
	type: TypeKey;
}

interface CommentUser {
	id: number;
}

interface CommentLike {
	userId: number;
}

interface CommentEntity {
	id: number;
	user: CommentUser;
	likes: CommentLike[];
}

interface CommentsContentProps {
	comments: CommentEntity[];
	isActive?: boolean;
	allowReply?: boolean;
	placeholder?: string;
	parentComment?: CommentEntity | null;
	onSuccess?: () => void;
	setSort: (sort: SortKey) => void;
	setType: (type: Exclude<TypeKey, null>) => void;
	filters: CommentFilters;
	onAddComment: (params: {
		comment: string;
		parentId?: number;
		receiverId?: number;
		image?: unknown;
	}) => Promise<void>;
	onRemoveComment: (params: { commentId: number }) => Promise<void> | void;
	onToggleLike: (params: { id: number }) => Promise<void> | void;
}

function CommentsContent({
	comments,
	isActive,
	allowReply,
	placeholder,
	parentComment = null,
	onSuccess,
	setSort,
	setType,
	filters,
	onAddComment,
	onRemoveComment,
	onToggleLike,
}: CommentsContentProps) {
	const { t } = useTranslation();
	const [commentImage, setCommentImage] = useState<unknown>(null);

	const addComment = async (value: string) => {
		await onAddComment({
			comment: value,
			parentId: parentComment?.id,
			receiverId: parentComment?.user.id,
			image: commentImage,
		});
		onSuccess?.();
	};

	const renderComments = (): ReactNode =>
		comments.map((comment) => (
			<CommentItem
				comment={comment}
				key={comment.id}
				allowReply={allowReply}
				onRemoveComment={onRemoveComment}
				onAddComment={onAddComment}
				onToggleLike={onToggleLike}
			/>
		));

	const onSortClick = (sort: SortKey) => () => setSort(sort);
	const sortSelected = (sort: SortKey) => filters.sort === sort;

	const onTypeClick = (type: Exclude<TypeKey, null>) => () => setType(type);
	const typeSelected = (type: Exclude<TypeKey, null>) => filters.type === type;

	const renderHeader = () => {
		if (parentComment || (comments.length === 0 && !filters.type)) {
			return null;
		}

		return (
			<div className="flex items-center justify-between mb-12">
				<h3 className="size-16 m-0">{t("exam.comments.title", "Komentarze")}</h3>
				<div className="flex items-center gap-8">
					<MenuLUI
						icon={<ArrowUpDown className="icon-small" />}
						tooltip={t("tooltips.sortComments", "Sortowanie")}
						items={[
							{
								title: t("exam.comments.sort.newest", "Od najnowszych"),
								onClick: onSortClick("DESC"),
								selected: sortSelected("DESC"),
							},
							{
								title: t("exam.comments.sort.oldest", "Od najstarszych"),
								onClick: onSortClick("ASC"),
								selected: sortSelected("ASC"),
							},
							{
								title: t("exam.comments.sort.mostPopular", "Popularność"),
								onClick: onSortClick("LIKES"),
								selected: sortSelected("LIKES"),
							},
						]}
						closeAfterClick
					/>
					{isPes() && (
						<MenuLUI
							icon={
								<Badge
									color="secondary"
									variant="dot"
									invisible={!filters.type}
								>
									<Filter className="icon-small" />
								</Badge>
							}
							tooltip={t("tooltips.filterComments", "Filtry")}
							items={[
								{
									title: t(
										"exam.comments.filter.onlyEgzaminlek",
										"Tylko EgzaminLEK",
									),
									onClick: onTypeClick("BASE"),
									selected: typeSelected("BASE"),
								},
							]}
							closeAfterClick
						/>
					)}
				</div>
			</div>
		);
	};

	return (
		<div>
			{isActive && (
				<AddCommentField
					onSubmit={addComment}
					placeholder={placeholder}
					commentImage={commentImage}
					setCommentImage={setCommentImage}
					parent={parentComment}
				/>
			)}
			<div className={cx({ "mt-24": isActive })}>
				{renderHeader()}
				<List className={cx("py-0", "comments-list")}>{renderComments()}</List>
			</div>
		</div>
	);
}

export default CommentsContent;
